import { MedplumClient } from "@medplum/core";
import { Encounter } from "@medplum/fhirtypes";

import { applyEncounterServiceDateDetailsToEncounter } from "@/encounters/utils/encounter-service-date-form";
import { EncounterDetailsFormType } from "@/encounters/zod-schemas/encounter-answers";

export type UpdateAmendedEncounterDetailsResult =
  | { ok: true; data: Encounter }
  | { ok: false; reason: "missing_encounter_details" };

/**
 * Persists amended Type/Location/service date onto the Encounter resource so version
 * history reflects those fields (not QR alone).
 *
 * @param medplum Medplum client used to read and update the Encounter.
 * @param encounterId Encounter id to update.
 * @param encounterDetails Amendment form encounter details (template, type, location, service date).
 * @returns Explicit result with the updated Encounter, or missing_encounter_details when required fields are absent.
 */
export async function updateAmendedEncounterDetails(
  medplum: MedplumClient,
  encounterId: string,
  encounterDetails: Partial<EncounterDetailsFormType> | undefined,
): Promise<UpdateAmendedEncounterDetailsResult> {
  const encounterTemplate = encounterDetails?.encounterTemplate;
  const encounterType = encounterDetails?.encounterType;
  const encounterLocation = encounterDetails?.encounterLocation;

  if (!encounterTemplate || !encounterType || !encounterLocation) {
    return { ok: false, reason: "missing_encounter_details" };
  }

  const latestEncounter = await medplum.readResource("Encounter", encounterId);
  const updatedEncounter = applyEncounterServiceDateDetailsToEncounter(
    {
      ...latestEncounter,
      serviceType: { coding: [encounterTemplate] },
      type: [{ coding: [encounterType], text: encounterType.display }],
      class: encounterLocation,
    },
    encounterDetails,
  );

  const savedEncounter = await medplum.updateResource(updatedEncounter);
  return { ok: true, data: savedEncounter };
}
